/**
 * The 737 words the three-word paragraph ids (`_result_prev_signed`) draw from.
 *
 * WHY a screened list and not any dictionary
 *   A paragraph id is read and written by language models thousands of times
 *   per review, so every word must be one token, with its leading underscore,
 *   in both OpenAI encodings in use. A word that splits costs a token per id
 *   and gives the model a fragment boundary to garble.
 *
 * SCREENS every member passes
 *   1. lowercase, 3 to 7 letters
 *   2. `word` and `_word` each encode as exactly one token in `o200k_base` and
 *      in `cl100k_base`, measured with tiktoken 0.13.0, so a paragraph id
 *      `_word_word_word` is always three tokens
 *   3. no member is the plural of another member (`plate` and `plates` never
 *      both appear)
 *   4. no function words, abbreviations, or words a contract reader would take
 *      as content (money, parties, legal effect, harm)
 *   5. pairwise Levenshtein distance at least 2, so no two ids differ by a
 *      single character (`plate` and `plane` never both appear)
 *
 * EDIT WITH CARE
 *   Sorted and unique; `paragraph-bookmark-naming.test.ts` holds screens 1, 3
 *   and the sort and uniqueness to every run. Screens 2 and 5 are not checked
 *   by a test, so a word added by hand must be measured against both encodings
 *   and against every existing member before it is added.
 */
export const PARAGRAPH_ID_WORDS: readonly string[] = [
    "accept",
    "access",
    "action",
    "active",
    "actor",
    "actual",
    "adapter",
    "add",
    "added",
    "address",
    "adjust",
    "admin",
    "age",
    "air",
    "alarm",
    "album",
    "alert",
    "alias",
    "align",
    "alive",
    "allow",
    "allowed",
    "anchor",
    "angle",
    "answer",
    "app",
    "apply",
    "arch",
    "archive",
    "area",
    "array",
    "arrow",
    "art",
    "article",
    "artist",
    "assign",
    "atom",
    "atomic",
    "attach",
    "attempt",
    "audio",
    "avatar",
    "average",
    "avg",
    "axis",
    "backup",
    "ball",
    "banner",
    "bar",
    "basis",
    "batch",
    "begin",
    "best",
    "bias",
    "bid",
    "binary",
    "binding",
    "blank",
    "blob",
    "blue",
    "board",
    "body",
    "book",
    "booking",
    "boolean",
    "border",
    "bottom",
    "box",
    "branch",
    "brand",
    "break",
    "bridge",
    "browser",
    "bucket",
    "buffer",
    "bug",
    "build",
    "builder",
    "bulk",
    "bundle",
    "busy",
    "button",
    "byte",
    "cache",
    "cal",
    "called",
    "camera",
    "cancel",
    "canvas",
    "caption",
    "capture",
    "card",
    "case",
    "catalog",
    "cell",
    "chain",
    "change",
    "channel",
    "chat",
    "check",
    "checked",
    "child",
    "chip",
    "choice",
    "choose",
    "chunk",
    "circle",
    "city",
    "class",
    "clean",
    "cleanup",
    "clock",
    "clone",
    "closed",
    "cloud",
    "cluster",
    "code",
    "coin",
    "collect",
    "color",
    "column",
    "com",
    "command",
    "comment",
    "commit",
    "common",
    "compare",
    "compile",
    "complex",
    "compute",
    "config",
    "confirm",
    "connect",
    "console",
    "const",
    "context",
    "control",
    "convert",
    "cookie",
    "copy",
    "corner",
    "correct",
    "counter",
    "course",
    "cover",
    "create",
    "creator",
    "crop",
    "cross",
    "cube",
    "current",
    "cursor",
    "curve",
    "custom",
    "cut",
    "cycle",
    "daily",
    "dash",
    "data",
    "day",
    "debug",
    "decimal",
    "decode",
    "define",
    "degree",
    "delay",
    "delete",
    "dense",
    "density",
    "depart",
    "depend",
    "depth",
    "design",
    "detail",
    "detect",
    "dev",
    "device",
    "dialog",
    "digest",
    "digit",
    "dim",
    "direct",
    "disable",
    "disk",
    "display",
    "domain",
    "dot",
    "double",
    "draw",
    "drive",
    "due",
    "dynamic",
    "easy",
    "echo",
    "edge",
    "editor",
    "effect",
    "element",
    "email",
    "emit",
    "empty",
    "enable",
    "encode",
    "encrypt",
    "energy",
    "engine",
    "ent",
    "enter",
    "entity",
    "entries",
    "entry",
    "episode",
    "equal",
    "event",
    "exact",
    "exam",
    "example",
    "excerpt",
    "execute",
    "exist",
    "expand",
    "expect",
    "export",
    "extent",
    "extra",
    "extract",
    "eye",
    "fact",
    "factory",
    "false",
    "feature",
    "feed",
    "fetch",
    "field",
    "fill",
    "filter",
    "final",
    "find",
    "finish",
    "first",
    "fit",
    "fixed",
    "fixture",
    "flag",
    "flash",
    "flip",
    "float",
    "floor",
    "focus",
    "folder",
    "follow",
    "food",
    "footer",
    "form",
    "format",
    "formula",
    "forward",
    "found",
    "frame",
    "front",
    "fun",
    "future",
    "gain",
    "gallery",
    "game",
    "gap",
    "gateway",
    "gen",
    "general",
    "generic",
    "global",
    "goal",
    "gold",
    "grade",
    "graph",
    "gray",
    "green",
    "grid",
    "ground",
    "group",
    "growth",
    "guess",
    "half",
    "handler",
    "header",
    "heading",
    "heap",
    "height",
    "helper",
    "here",
    "hidden",
    "hide",
    "high",
    "hint",
    "history",
    "home",
    "host",
    "hour",
    "icon",
    "idle",
    "ignore",
    "image",
    "import",
    "include",
    "index",
    "indices",
    "info",
    "initial",
    "inner",
    "input",
    "ins",
    "insert",
    "inside",
    "install",
    "integer",
    "intro",
    "issue",
    "item",
    "job",
    "jump",
    "keep",
    "kernel",
    "key",
    "keyword",
    "known",
    "lab",
    "label",
    "land",
    "large",
    "last",
    "latest",
    "launch",
    "layout",
    "lazy",
    "leaf",
    "leave",
    "left",
    "leg",
    "length",
    "letter",
    "level",
    "library",
    "light",
    "limit",
    "line",
    "linear",
    "listing",
    "load",
    "loaded",
    "loading",
    "local",
    "locator",
    "locked",
    "logged",
    "logging",
    "logic",
    "logo",
    "long",
    "lookup",
    "loop",
    "loss",
    "low",
    "machine",
    "macro",
    "magic",
    "major",
    "man",
    "manager",
    "manual",
    "mapping",
    "margin",
    "mark",
    "marker",
    "mass",
    "master",
    "math",
    "matrix",
    "measure",
    "media",
    "medium",
    "memory",
    "menu",
    "merge",
    "mesh",
    "message",
    "meta",
    "meter",
    "method",
    "metric",
    "middle",
    "minor",
    "minus",
    "minutes",
    "missing",
    "mix",
    "mobile",
    "mod",
    "model",
    "modify",
    "module",
    "monitor",
    "month",
    "motion",
    "motor",
    "mount",
    "mouse",
    "movie",
    "music",
    "named",
    "native",
    "needed",
    "nested",
    "net",
    "network",
    "noise",
    "non",
    "normal",
    "note",
    "notify",
    "number",
    "numeric",
    "object",
    "oct",
    "offset",
    "online",
    "open",
    "option",
    "origin",
    "outer",
    "outline",
    "output",
    "overlay",
    "owned",
    "pack",
    "package",
    "packet",
    "padding",
    "panel",
    "parent",
    "partial",
    "pat",
    "pattern",
    "pause",
    "peak",
    "peer",
    "pending",
    "percent",
    "period",
    "phone",
    "photo",
    "phrase",
    "pic",
    "picture",
    "piece",
    "pipe",
    "pitch",
    "pixel",
    "place",
    "plain",
    "play",
    "player",
    "plot",
    "plugin",
    "plural",
    "plus",
    "point",
    "pointer",
    "polygon",
    "pool",
    "pop",
    "port",
    "pose",
    "power",
    "pre",
    "predict",
    "prefix",
    "prepare",
    "present",
    "press",
    "pressed",
    "preview",
    "primary",
    "prime",
    "prior",
    "probe",
    "problem",
    "proc",
    "process",
    "profile",
    "program",
    "project",
    "prompt",
    "proxy",
    "pub",
    "publish",
    "pull",
    "push",
    "quality",
    "queries",
    "query",
    "queue",
    "quote",
    "radio",
    "radius",
    "raise",
    "ram",
    "random",
    "range",
    "rank",
    "rate",
    "rating",
    "ready",
    "real",
    "reason",
    "receive",
    "recent",
    "recipe",
    "record",
    "red",
    "reduce",
    "refresh",
    "region",
    "regular",
    "related",
    "release",
    "reload",
    "remote",
    "removed",
    "render",
    "repeat",
    "replace",
    "reply",
    "report",
    "request",
    "require",
    "reset",
    "resize",
    "restore",
    "result",
    "resume",
    "retry",
    "return",
    "reverse",
    "ring",
    "robot",
    "roll",
    "rotate",
    "route",
    "rule",
    "runner",
    "running",
    "runtime",
    "safe",
    "sample",
    "saved",
    "scale",
    "scan",
    "scene",
    "scheme",
    "scope",
    "screen",
    "script",
    "scroll",
    "search",
    "sec",
    "second",
    "section",
    "sector",
    "secure",
    "seek",
    "segment",
    "select",
    "self",
    "send",
    "sensor",
    "serial",
    "series",
    "server",
    "session",
    "setting",
    "setup",
    "shadow",
    "shape",
    "sheet",
    "shell",
    "shift",
    "shop",
    "short",
    "shuffle",
    "sign",
    "signal",
    "signup",
    "single",
    "sink",
    "size",
    "skill",
    "skip",
    "sleep",
    "slice",
    "slider",
    "small",
    "snap",
    "sock",
    "socket",
    "soft",
    "sol",
    "sorted",
    "source",
    "space",
    "spawn",
    "special",
    "species",
    "speed",
    "spin",
    "spinner",
    "split",
    "sprite",
    "square",
    "stack",
    "stage",
    "stamp",
    "start",
    "started",
    "static",
    "station",
    "status",
    "step",
    "storage",
    "story",
    "str",
    "stream",
    "stride",
    "string",
    "strip",
    "struct",
    "style",
    "subject",
    "submit",
    "subplot",
    "success",
    "suffix",
    "suite",
    "sum",
    "summary",
    "super",
    "support",
    "surface",
    "switch",
    "symbol",
    "sync",
    "sys",
    "system",
    "table",
    "tag",
    "tail",
    "take",
    "target",
    "task",
    "team",
    "testing",
    "text",
    "texture",
    "theme",
    "thread",
    "throw",
    "thumb",
    "tile",
    "timer",
    "tip",
    "today",
    "token",
    "toolbar",
    "topic",
    "total",
    "touch",
    "trace",
    "tracker",
    "train",
    "tree",
    "trial",
    "trigger",
    "trim",
    "truth",
    "try",
    "turn",
    "type",
    "uniform",
    "union",
    "unique",
    "unit",
    "unknown",
    "unlock",
    "unused",
    "update",
    "upgrade",
    "upload",
    "upper",
    "uri",
    "usage",
    "usb",
    "user",
    "valid",
    "value",
    "vehicle",
    "verbose",
    "verify",
    "version",
    "vertex",
    "video",
    "view",
    "virtual",
    "visible",
    "visit",
    "visual",
    "voice",
    "volume",
    "wait",
    "walk",
    "warning",
    "water",
    "wave",
    "weather",
    "web",
    "widget",
    "width",
    "win",
    "window",
    "wire",
    "word",
    "worker",
    "wrap",
    "write",
    "written",
    "year",
    "zero",
    "zone",
    "zoom",
];
